if (!customElements.get('custom-measurements')) {
  customElements.define('custom-measurements', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.product = this.closest('product-form');
      this.cartForm = document.getElementById(this.dataset.formId);
      if (!this.cartForm) return;
      this.variants = JSON.parse(this.querySelector('script').textContent);
      this.dialog = this.querySelector('dialog');
      this.measurementForm = this.querySelector('form');
      this.fields = [...this.querySelectorAll('[data-measurement]')];
      this.unit = this.querySelector('[name="unit"]');
      this.properties = document.createElement('div');
      this.cartForm.append(this.properties);
      this.saved = false;
      this.previousUnit = 'cm';
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.measurementForm.addEventListener('focusin', (event) => {
        if (event.target.matches('[data-measurement]')) {
          this.activeMeasurement = event.target;
          this.updateGuide();
        }
      }, options);
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.product.addEventListener('on:variant:change', () => this.sync(true), options);
      this.product.addEventListener('click', (event) => {
        const option = event.target.closest('variant-picker input.js-option, variant-picker .custom-select__option');
        if (option && /custom/i.test(option.value || option.dataset.value || '')) {
          // Let the picker finish selecting the variant before opening the dialog.
          queueMicrotask(() => this.sync(true));
        }
      }, options);
      this.cartForm.addEventListener('submit', (event) => {
        this.sync(false);
        if (this.custom && (!this.saved || !this.fields.every((field) => field.validity.valid))) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.open();
        }
      }, { ...options, capture: true });
      this.measurementForm.addEventListener('input', (event) => {
        if (event.target.matches('[data-measurement]')) this.clearFieldError(event.target);
        this.saved = false;
        this.updateProperties();
      }, options);
      this.unit.addEventListener('change', () => {
        const factor = this.unit.value === 'in' ? 1 / 2.54 : 2.54;
        if (this.previousUnit !== this.unit.value) {
          this.fields.forEach((field) => {
            if (field.value && Number.isFinite(field.valueAsNumber)) field.value = String(Math.round(field.valueAsNumber * factor * 100) / 100);
          });
        }
        this.previousUnit = this.unit.value;
        this.querySelectorAll('[data-unit]').forEach((label) => { label.textContent = this.unit.value; });
        this.fields.forEach((field) => {
          const divisor = this.unit.value === 'in' ? 2.54 : 1;
          field.min = String(Math.floor(Number(field.dataset.minCm) / divisor * 100) / 100);
          field.max = String(Math.ceil(Number(field.dataset.maxCm) / divisor * 100) / 100);
          this.clearFieldError(field);
        });
        this.saved = false;
        this.updateProperties();
      }, options);
      this.querySelectorAll('[data-set-unit]').forEach((button) => {
        button.addEventListener('click', () => {
          if (this.unit.value === button.dataset.setUnit) return;
          this.unit.value = button.dataset.setUnit;
          this.unit.dispatchEvent(new Event('change'));
          this.querySelectorAll('[data-set-unit]').forEach((item) => {
            item.setAttribute('aria-pressed', String(item === button));
          });
        }, options);
      });
      this.measurementForm.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const invalidFields = this.fields.filter((field) => !this.validateField(field));
        if (invalidFields.length) {
          invalidFields[0].focus();
          return;
        }
        this.saved = true;
        this.updateProperties();
        this.dialog.close();
        const addButton = this.cartForm.querySelector('[name="add"]');
        if (this.custom && addButton && !addButton.disabled && addButton.getAttribute('aria-disabled') !== 'true') {
          this.cartForm.requestSubmit(addButton);
        }
      }, options);
      this.sync(false);
    }
    sync(shouldOpen) {
      const variant = this.variants.find((item) => String(item.id) === this.cartForm.elements.id.value);
      this.custom = Boolean(variant?.options.some((value) => /custom/i.test(value)));
      this.product.querySelectorAll('.shopify-payment-button').forEach((button) => { button.hidden = this.custom; });
      this.updateProperties();
      if (this.custom && shouldOpen) this.open();
      if (!this.custom && this.dialog.open) this.dialog.close();
    }
    updateProperties() {
      this.updateGuide();
      const requiredFields = this.fields.filter((field) => field.required);
      const completed = requiredFields.filter((field) => field.value && field.validity.valid).length;
      this.querySelector('[data-progress]').textContent = `${completed} of ${requiredFields.length} required measurements completed`;
      this.properties.replaceChildren();
      if (this.custom && this.saved && this.fields.every((field) => field.validity.valid)) {
        this.fields.forEach((field) => {
          if (!field.value) return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${field.name}]`;
          input.value = `${field.value} ${this.unit.value}`;
          this.properties.append(input);
        });
      }
    }
    clearFieldError(field) {
      field.removeAttribute('aria-invalid');
      const error = field.closest('.custom-measurements__field').querySelector('[data-error]');
      error.hidden = true;
      error.textContent = '';
    }
    validateField(field) {
      const valid = field.validity.valid;
      const error = field.closest('.custom-measurements__field').querySelector('[data-error]');
      field.setAttribute('aria-invalid', String(!valid));
      error.hidden = valid;
      error.textContent = valid ? '' : field.validity.valueMissing && !field.validity.badInput
        ? 'Please enter this measurement.'
        : `Enter a value between ${field.min} and ${field.max} ${this.unit.value}.`;
      return valid;
    }
    open() {
      if (!this.dialog.open) {
        this.dialog.showModal();
        this.querySelector('[data-heading]').focus({ preventScroll: true });
      }
    }
    updateGuide() {
      const field = this.activeMeasurement;
      const view = field?.name === 'Back length' ? 'back' : 'front';
      this.querySelectorAll('[data-body-view]').forEach((part) => {
        part.style.display = part.dataset.bodyView === view ? '' : 'none';
      });
      this.querySelector('[data-body-model]').setAttribute('aria-label', `${view === 'back' ? 'Back' : 'Front'} view showing measurement positions`);
      this.querySelectorAll('[data-guide-region]').forEach((region) => {
        region.classList.toggle('is-active', region.dataset.guideRegion === field?.name);
      });
      this.fields.forEach((input) => {
        input.closest('.custom-measurements__field').classList.toggle('is-active', input === field);
      });
      this.querySelector('[data-guide-label]').textContent = field?.name || 'Select a measurement';
      this.querySelector('[data-guide-value]').textContent = field?.value && Number.isFinite(field.valueAsNumber) ? `${field.value} ${this.unit.value}` : '—';
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.properties?.remove();
      this.initialized = false;
    }
  });
}
